import React, { useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { FileSearch, Printer } from 'lucide-react';

/**
 * Code Gaps Report
 *
 * Groups every item and kit code by its alpha prefix (e.g. "WC", "M") and lists
 * the missing numeric suffixes between the lowest and highest used numbers in
 * that prefix. Useful when assigning a new code to a freshly onboarded item —
 * pick from the gaps before extending the sequence.
 *
 * The report runs entirely against the items / kits already loaded into
 * AppContext, so no backend round-trip is required.
 */

// Parse a code like "WC0034" -> { prefix: "WC", num: 34, width: 4 }
// Returns null for codes that don't follow the <letters><digits> pattern.
function parseCode(code) {
  const m = /^([A-Za-z]+)(\d+)$/.exec(code || '');
  if (!m) return null;
  return { prefix: m[1].toUpperCase(), num: parseInt(m[2], 10), width: m[2].length };
}

// Given a list of codes, group by prefix and compute the missing numbers
// between min and max. Returns an array of report rows sorted by prefix.
//
// `prefixFloors` lets callers force a lower bound for a given prefix — e.g.
// `{ WC: 1001 }` on kits keeps WC1001 in scope as a "gap" even if all real
// kit codes start at WC1002. Useful when you want to open up a slot below
// the current natural minimum.
function buildGapReport(codes, prefixFloors = {}) {
  const groups = new Map();
  const unparsable = [];

  for (const raw of codes) {
    const parsed = parseCode(raw);
    if (!parsed) {
      if (raw) unparsable.push(raw);
      continue;
    }
    const { prefix, num, width } = parsed;
    if (!groups.has(prefix)) {
      groups.set(prefix, { prefix, nums: new Set(), maxWidth: width });
    }
    const g = groups.get(prefix);
    g.nums.add(num);
    if (width > g.maxWidth) g.maxWidth = width;
  }

  const rows = [];
  for (const { prefix, nums, maxWidth } of groups.values()) {
    const sorted = [...nums].sort((a, b) => a - b);
    const floor = prefixFloors[prefix];
    const min = floor !== undefined ? Math.min(sorted[0], floor) : sorted[0];
    const max = sorted[sorted.length - 1];
    const missing = [];
    for (let n = min; n <= max; n++) {
      if (!nums.has(n)) missing.push(n);
    }
    rows.push({
      prefix,
      min,
      max,
      width: maxWidth,
      used: sorted.length,
      total: max - min + 1,
      missing,
    });
  }

  rows.sort((a, b) => a.prefix.localeCompare(b.prefix));
  return { rows, unparsable };
}

function formatCode(prefix, num, width) {
  return `${prefix}${String(num).padStart(width, '0')}`;
}

function GapSection({ title, report }) {
  if (report.rows.length === 0) {
    return (
      <div className="surface rounded-lg p-6">
        <div className="text-xs text-neutral-500">No codes to analyse for {title.toLowerCase()}.</div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={`gap-section-${title.toLowerCase()}`}>
      {report.rows.map((row) => {
        const first = formatCode(row.prefix, row.min, row.width);
        const last = formatCode(row.prefix, row.max, row.width);
        return (
          <div
            key={row.prefix}
            className="surface rounded-lg p-5"
            data-testid={`gap-row-${title.toLowerCase()}-${row.prefix}`}
          >
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-amber-400 mb-1">
                  Prefix · {row.prefix}
                </div>
                <div className="text-sm text-neutral-200">
                  Between{' '}
                  <span className="font-mono text-white">{first}</span> and{' '}
                  <span className="font-mono text-white">{last}</span>
                </div>
              </div>
              <div className="text-[11px] text-neutral-500 flex items-center gap-3">
                <span>
                  <span className="text-white font-medium">{row.used}</span> used
                </span>
                <span className="text-neutral-700">·</span>
                <span>
                  <span className="text-amber-400 font-medium">{row.missing.length}</span> gap
                  {row.missing.length === 1 ? '' : 's'}
                </span>
                <span className="text-neutral-700">·</span>
                <span>
                  range {row.total}
                </span>
              </div>
            </div>

            {row.missing.length === 0 ? (
              <div className="text-[11px] text-neutral-500 italic">
                No gaps — this prefix is fully sequential.
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {row.missing.map((n) => (
                  <span
                    key={n}
                    className="font-mono text-[11px] px-2 py-1 rounded border border-amber-500/20 bg-amber-500/5 text-amber-300"
                    data-testid={`gap-code-${formatCode(row.prefix, n, row.width)}`}
                  >
                    {formatCode(row.prefix, n, row.width)}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {report.unparsable.length > 0 && (
        <div className="surface rounded-lg p-4">
          <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 mb-2">
            Unparsable codes (skipped)
          </div>
          <div className="flex flex-wrap gap-1.5">
            {report.unparsable.map((c) => (
              <span
                key={c}
                className="font-mono text-[11px] px-2 py-1 rounded border border-neutral-800 text-neutral-400"
              >
                {c}
              </span>
            ))}
          </div>
          <div className="text-[10px] text-neutral-600 mt-2">
            These don&apos;t match the <span className="font-mono">PREFIX####</span> pattern, so they
            can&apos;t be slotted into a gap sequence.
          </div>
        </div>
      )}
    </div>
  );
}

export default function CodeGapsReport() {
  const { items, kits } = useApp();

  const itemReport = useMemo(() => buildGapReport(items.map((i) => i.code)), [items]);
  // Kits: force WC1001 into the search range so it appears as an open slot
  // even though the current natural minimum is WC1002.
  const kitReport = useMemo(() => buildGapReport(kits.map((k) => k.code), { WC: 1001 }), [kits]);

  return (
    <div className="space-y-8" data-testid="code-gaps-report-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-amber-400 mb-1">
            Reports
          </div>
          <h1 className="text-2xl text-white flex items-center gap-2">
            <FileSearch size={20} className="text-amber-400" /> Code Gaps
          </h1>
          <p className="text-xs text-neutral-500 mt-1 max-w-xl">
            Unused code numbers between the lowest and highest in each prefix. Pick from
            these when assigning a new item or kit to keep the sequence tight.
          </p>
        </div>
        <button
          onClick={() => window.print()}
          className="btn-amber rounded-md px-3 py-1.5 text-xs flex items-center gap-2"
          data-testid="code-gaps-print"
        >
          <Printer size={13} /> Print report
        </button>
      </div>

      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-3">
          Gear / Items
        </div>
        <GapSection title="Items" report={itemReport} />
      </div>

      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-3">
          Kits
        </div>
        <GapSection title="Kits" report={kitReport} />
      </div>
    </div>
  );
}
